/*
 * Copyright (c) 2026. Orion Analytics. All Rights Reserved
 */

/*
 * Orion Analytics
 * Risk Dashboard
 */

import React, {
    useMemo,
    useState
} from "react";

import Plot from "react-plotly.js";

const TIMEFRAMES = {
    "1M": 30,
    "3M": 90,
    "6M": 180,
    "1Y": 365
};

export default function RiskAppetite({
    histData
}) {

    const DEFAULT_SELECTION = [
        "NIFTY 50",
        "NASDAQ",
        "S&P500",
        "Gold",
        "WTI_Crude"
    ];

    const [timeframe, setTimeframe] =
        useState("1Y");

    const [selected, setSelected] =
        useState(DEFAULT_SELECTION);

    const universe = [

        "NIFTY 50",
        "NASDAQ",
        "S&P500",
        "Gold",
        "WTI_Crude",
        "USDINR",
        "INDIA VIX"
    ];

    const latestValues = useMemo(() => {

        const result = {};

        universe.forEach(symbol => {

            const rows =
                histData
                    .filter(
                        r =>
                            r.symbol === symbol
                    )
                    .sort(
                        (a, b) =>
                            new Date(a.date) -
                            new Date(b.date)
                    );

            if (rows.length) {

                result[symbol] =
                    rows[
                        rows.length - 1
                        ].close;

            }

        });

        return result;

    }, [histData]);

    const riskRegime = useMemo(() => {

        const vix =
            latestValues[
                "INDIA VIX"
                ];

        if (!vix)
            return "Unknown";

        if (vix < 15)
            return "Risk On";

        if (vix < 20)
            return "Neutral";

        return "Risk Off";

    }, [latestValues]);

    const chartData = useMemo(() => {

        const cutoff =
            Date.now() -
            TIMEFRAMES[
                timeframe
                ] *
            86400000;

        return selected.map(symbol => {

            const rows =
                histData
                    .filter(
                        r =>
                            r.symbol === symbol &&
                            new Date(
                                r.date
                            )
                                .getTime() >=
                            cutoff
                    )
                    .sort(
                        (a, b) =>
                            new Date(a.date) -
                            new Date(b.date)
                    );

            if (!rows.length)
                return null;

            const base =
                rows[0].close;

            return {

                x:
                    rows.map(
                        r => r.date
                    ),

                y:
                    rows.map(
                        r =>
                            (
                                (
                                    r.close -
                                    base
                                ) /
                                base
                            ) *
                            100
                    ),

                type:
                    "scatter",

                mode:
                    "lines",

                name:
                    symbol
            };

        }).filter(Boolean);

    }, [
        histData,
        selected,
        timeframe
    ]);

    const toggleSymbol =
        symbol => {

            setSelected(prev =>

                prev.includes(symbol)

                    ? prev.filter(
                        s =>
                            s !== symbol
                    )

                    : [
                        ...prev,
                        symbol
                    ]

            );

        };

    return (

        <div
            className="
                bg-slate-950
                border
                border-slate-800
                rounded-3xl
                p-6
            "
        >

            {/* METRIC CARDS */}

            <div
                className="
                    grid
                    grid-cols-2
                    md:grid-cols-4
                    gap-4
                    mb-6
                "
            >

                <MetricCard
                    title="INDIA VIX"
                    value={
                        latestValues[
                            "INDIA VIX"
                            ]
                    }
                />

                <MetricCard
                    title="USDINR"
                    value={
                        latestValues[
                            "USDINR"
                            ]
                    }
                />

                <MetricCard
                    title="Gold"
                    value={
                        latestValues[
                            "Gold"
                            ]
                    }
                />

                <MetricCard
                    title="Regime"
                    value={riskRegime}
                />

            </div>

            {/* TIMEFRAME */}

            <div
                className="
                    flex
                    gap-2
                    mb-6
                "
            >

                {
                    Object.keys(
                        TIMEFRAMES
                    ).map(tf => (

                        <button
                            key={tf}
                            onClick={() =>
                                setTimeframe(
                                    tf
                                )
                            }
                            className={`px-3 py-2 rounded-xl text-sm
                            ${
                                timeframe === tf
                                    ? "bg-emerald-500 text-white"
                                    : "bg-slate-900 text-slate-300"
                            }`}
                        >
                            {tf}
                        </button>

                    ))
                }

            </div>

            <div
                className="
                    flex
                    gap-6
                "
            >

                {/* CHART */}

                <div
                    className="
                        flex-1
                    "
                >

                    <Plot
                        data={chartData}
                        layout={{
                            height: 650,

                            paper_bgcolor:
                                "#020617",

                            plot_bgcolor:
                                "#020617",

                            font: {
                                color:
                                    "#cbd5e1"
                            },

                            xaxis: {
                                showgrid:
                                    false
                            },

                            yaxis: {
                                title:
                                    "% Return",
                                gridcolor:
                                    "#1e293b"
                            },

                            margin: {
                                l: 50,
                                r: 20,
                                t: 20,
                                b: 40
                            }
                        }}
                        config={{
                            displayModeBar:
                                false,
                            responsive:
                                true
                        }}
                        style={{
                            width:
                                "100%"
                        }}
                    />

                </div>

                {/* SIDEBAR */}

                <div
                    className="
                        w-52
                        border-l
                        border-slate-800
                        pl-4
                    "
                >

                    {
                        universe.map(
                            symbol => (

                                <button
                                    key={
                                        symbol
                                    }
                                    onClick={() =>
                                        toggleSymbol(
                                            symbol
                                        )
                                    }
                                    className={`
                                        w-full
                                        text-left
                                        px-3
                                        py-2
                                        rounded-lg
                                        mb-2
                                        text-sm
                                        ${
                                            selected.includes(
                                                symbol
                                            )
                                                ? "bg-emerald-500 text-white"
                                                : "bg-slate-900 text-slate-300"
                                        }
                                    `}
                                >
                                    {symbol}
                                </button>

                            )
                        )
                    }

                </div>

            </div>

        </div>

    );

}

function MetricCard({
    title,
    value
}) {

    return (

        <div
            className="
                bg-slate-900
                border
                border-slate-800
                rounded-2xl
                p-4
            "
        >

            <div
                className="
                    text-xs
                    uppercase
                    text-slate-500
                "
            >
                {title}
            </div>

            <div
                className="
                    text-lg
                    font-bold
                    text-white
                "
            >
                {typeof value === "number"
                    ? value.toFixed(2)
                    : value}
            </div>

        </div>

    );

}